/**
 * useAiBuddies — the selectable AI companions (POST /Agent/AgentsList).
 *
 * The endpoint is paged, so the roster is accumulated page by page. Only the
 * newest request may write: a refresh issued while an older page is still in
 * flight would otherwise append that page onto the fresh list.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import aiBuddyService from '../api/services/aiBuddyService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { AIBuddy } from '../types/user';

export interface UseAiBuddiesResult {
  buddies: AIBuddy[];
  loading: boolean;
  refreshing: boolean;
  /** True while a page after the first is being fetched. */
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  /**
   * A page-fetch failure, kept apart from `error` so the roster already on
   * screen survives it and the caller can offer a retry in place of the
   * infinite-scroll footer.
   */
  loadMoreError: string;
  loaded: boolean;
  refresh: () => void;
  loadMore: () => void;
}

export const useAiBuddies = (): UseAiBuddiesResult => {
  const [buddies, setBuddies] = useState<AIBuddy[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);

  const load = useCallback(async (pageNumber: number, mode: 'initial' | 'refresh' | 'more') => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (mode === 'refresh') setRefreshing(true);
    else if (mode === 'more') setLoadingMore(true);
    else setLoading(true);
    // A reload replaces the roster, so a stale page failure no longer applies.
    setLoadMoreError('');
    if (mode !== 'more') setError('');

    try {
      const response = await aiBuddyService.getAIBuddies({ pageNumber });
      if (requestIdRef.current !== requestId) return;
      const page = response.data ?? [];
      setBuddies(prev => {
        if (mode === 'more') {
          const known = new Set(prev.map(buddy => buddy.id));
          return [...prev, ...page.filter(buddy => !known.has(buddy.id))];
        }
        return page;
      });
      pageRef.current = pageNumber;
      hasMoreRef.current = response.hasMore;
      setHasMore(response.hasMore);
    } catch (caught) {
      if (requestIdRef.current !== requestId) return;
      const message = getApiErrorMessage(caught as ApiError);
      if (mode === 'more') {
        // A failed page is not the end of the roster: leaving hasMore alone
        // keeps loadMore callable so the retry can actually fire.
        setLoadMoreError(message);
        return;
      }
      setError(message);
      if (mode === 'initial') setBuddies([]);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      inFlightRef.current = false;
      if (requestIdRef.current === requestId) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
        setLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    load(1, 'initial').catch(() => undefined);
  }, [load]);

  const refresh = useCallback(() => {
    load(1, 'refresh').catch(() => undefined);
  }, [load]);

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || inFlightRef.current) return;
    load(pageRef.current + 1, 'more').catch(() => undefined);
  }, [load]);

  return {
    buddies,
    loading,
    refreshing,
    loadingMore,
    hasMore,
    error,
    loadMoreError,
    loaded,
    refresh,
    loadMore,
  };
};

export default useAiBuddies;
